<template>
  <v-select
    ref="refRole"
    v-model="roles"
    :items="props.readonly || props.disabled ? undefined : roleList"
    :rules="props.rules"
    :prepend-inner-icon="props.prependInnerIcon"
    variant="outlined"
    label="Roles"
    item-props
    item-title="name"
    item-value="code"
    return-object
    :menu-icon="props.readonly || props.disabled ? null : 'mdi-menu-down'"
    :clearable="!props.readonly && !props.disabled"
    class="py-2"
    :loading="getIsLoadingAvailableRoles"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :multiple="props.multiple"
    @click:clear="refreshRoles()"
    @update:model-value="(event) => {
      disableRoles()
      emit('update:modelValue', event)
    }"
  >
    <template #prepend-item>
      <v-text-field
        v-model="roleQuery"
        density="compact"
        label="Search roles"
        prepend-inner-icon="mdi-magnify"
        variant="solo-filled"
        class="ma-2"
        flat
        hide-details
        @update:modelValue="filterRoles"
        @mousedown.stop
      />
      <v-divider class="mt-2" />
    </template>
    <template #selection="{ item, index }">
      <v-chip
        v-if="index < 3"
        color="medium-emphasis"
        border="thin opacity-25 medium-emphasis"
        class="px-2 py-3"
        label
        :closable="!props.readonly && !props.disabled"
        @click:close="() => {
          roles.splice(index, 1)
          emit('update:modelValue', roles)
        }"
      >
        {{ item.raw.name }}
      </v-chip>
      <span v-if="index == 3" class="text-grey text-caption align-self-center">
        (+{{ roles.length - 2 }} others)
      </span>
    </template>
  </v-select>
</template>


<script setup>
import { storeToRefs } from 'pinia'

import { useUser } from '@/stores/user'
import { watch } from 'vue'

const userStore = useUser()


const { getRoles, getIsLoadingAvailableRoles, getFetchLinkFrequency, getFetchAvailableRolesInterval } = storeToRefs(userStore)
const { fetchRoles, setFetchAvailableRolesInterval } = userStore

const emit = defineEmits(['update:modelValue', 'update:ref'])

const props = defineProps({
  modelValue: {
    default: null,
    type: Object,
    required: false
  },
  items: {
    default: undefined,
    type: Array,
    required: false
  },
  rules: {
    default: undefined,
    type: Array,
    required: false
  },
  errorMessages: {
    default: undefined,
    type: Array,
    required: false
  },
  prependInnerIcon: {
    default: 'mdi-shield-account-variant',
    type: String,
    required: false
  },
  disabled: {
    default: false,
    type: Boolean,
    required: false
  },
  readonly: {
    default: false,
    type: Boolean,
    required: false
  },
  multiple: {
    default: true,
    type: Boolean,
    required: false
  },
  separateLearner: {
    default: false,
    type: Boolean,
    required: false
  },
  stripCurrent: {
    default: true,
    type: Boolean,
    required: false
  },
  stripColor: {
    default: true,
    type: Boolean,
    required: false
  }
})

const roles = ref(props.modelValue)
const availableRoles = ref(props.items)
const roleList = ref([])
const roleQuery = ref(null)
const refRole = ref(null)

watch(refRole, (c) => emit('update:ref', c))


const disableRoles = () => {
  if (
    props.separateLearner == true &&
    Array.isArray(roleList.value) &&
    roleList.value.length > 0 &&
    Array.isArray(roles.value)
  ) {
    if (roles.value.length == 1) {
      if (roles.value[0].code === 'LRN')
        roleList.value = roleList.value.map((r) => {
          if (r.code != 'LRN')
            return { ...r, disabled: true }
          else
            return r
        })
      else
        roleList.value = roleList.value.map((r) => {
          if (r.code == 'LRN')
            return { ...r, disabled: true }
          else
            return r
        })
    } else if (roles.value.length == 0)
      roleList.value = roleList.value.map((r) => {
        if (Object.hasOwn(r, 'disabled'))
          delete r.disabled
        return r
      })

  }
}


const filterRoles = () => {
  if (availableRoles.value != null) {
    let _roles = [...availableRoles.value]
    const _roleQuery = roleQuery.value?.replace(/^\s+|\s+$/g, '').toLowerCase() || ''
    if (_roleQuery != '')
      _roles = _roles.filter(({ name, code }) => {
        return name.toLowerCase().includes(_roleQuery) ||
          code.toLowerCase().includes(_roleQuery)
      })
    roleList.value = [..._roles]
  } else roleList.value = []
}

const setItems = () => {
  if (props.items != null) {
    availableRoles.value = [...props.items]
    filterRoles()
    return
  }
  if (getRoles.value != null) {
    const isStrip = Object.hasOwn(props, 'stripCurrent') || Object.hasOwn(props, 'stripColor')
    const stripCurrent = Object.hasOwn(props, 'stripCurrent') ? props.stripCurrent : true
    const stripColor = Object.hasOwn(props, 'stripColor') ? props.stripColor : true
    availableRoles.value = isStrip
      ? [...getRoles.value].map((r) => {
        if (stripCurrent && Object.hasOwn(r, 'current'))
          delete r.current
        if (stripColor && Object.hasOwn(r, 'color'))
          delete r.color
        return r
      })
      : [...getRoles.value]
    filterRoles()
  }
}

const refreshRoles = async () => {
  await fetchRoles(false)
  setItems()
}

const setFetchers = async () => {
  if (!getIsLoadingAvailableRoles.value) {
    await Promise.all([fetchRoles()])
    setItems()
    if (getFetchAvailableRolesInterval.value == null && props.items == null)
      setFetchAvailableRolesInterval(setInterval(
        refreshRoles,
        getFetchLinkFrequency.value
      ))
  }
}

watch(() => props.items, () => {
  setItems()
}, { deep: true })

watch(getIsLoadingAvailableRoles, (c) => {
  if (!c && props.items == null && getRoles.value == null && getFetchAvailableRolesInterval.value == null)
    setFetchers()
 setItems()
}, { once: true })

onBeforeMount(() => {
  if (props.items == null && getRoles.value == null)
    setFetchers()
  setItems()
})

onBeforeUnmount(() => {
  if (getFetchAvailableRolesInterval.value != null)
    setFetchAvailableRolesInterval(null)

  roles.value = null
  availableRoles.value = null
  roleList.value = []
  roleQuery.value = null
  refRole.value = null
})
</script>